/**
 * @file FormEdit/index.tsx
 *
 * Componente principal de edição/criação de formulários do módulo NimberForms.
 *
 * Este componente é responsável por:
 *  - Carregar um Template de formulário (e a sua última Instância e Perguntas) a partir de um GUID;
 *  - Permitir a criação de um novo Template (quando o GUID é inválido/null);
 *  - Servir de "container" para a edição do título/descrição do template;
 *  - Servir de "container" para a grelha de perguntas ({@link ManageQuestionsGrid});
 *  - Disparar a criação de uma nova Instância de formulário (via diálogo {@link CreateInstance});
 *  - Persistir as alterações (criar/atualizar template, criar/atualizar/eliminar perguntas);
 *  - Disponibilizar o link público do formulário (para cópia).
 *
 * Fluxo geral:
 *  1. O componente recebe um `guid` opcional. Se for um GUID válido, considera-se modo "edição";
 *     caso contrário, é "criação" e gera-se um novo UUID local no store.
 *  2. As queries `useFormTemplateById`, `useGetInstanceByFilter` e `useGetQuestionByFilter` carregam
 *     os dados em cascata (a instância e as perguntas dependem do templateId).
 *  3. Os useEffects sincronizam os dados carregados com o store global ({@link useFormsEditStore}).
 *  4. Toda a manipulação local (adicionar/editar/remover perguntas, opções) é feita no store, e só é
 *     persistida quando o utilizador clica em "Salvar/Adicionar".
 *
 * Dependências importantes:
 *  - {@link useFormsEditStore}: store Zustand que mantém o estado da edição (template, instance,
 *    perguntas, opções, perguntas adicionadas/removidas em runtime).
 *  - Hooks de mutation/query do React Query para template, instance e questions.
 */

import { FormInstance, FormInstanceUpdate } from "domain/entities/form-instance";
import { FormQuestion } from "domain/entities/form-question";
import { FormInstanceInputFilter, QuestionInputFilter } from "domain/models/nimberforms-input-filters";
import Spinner from "presentation/components/Shared/Spinner";
import { useCreateFormsInstanceMutation, useGetInstanceByFilter, useUpdateFormsInstanceMutation } from "presentation/hooks/use-query-formsinstance";
import { useCreateTemplateMutation, useFormTemplateById, useUpdateTemplateMutation } from "presentation/hooks/use-query-formtemplate";
import { useCreateQuestionMutation, useDeleteQuestionMutation, useGetQuestionByFilter, useUpdateQuestionMutation } from "presentation/hooks/use-query-questions";
import { useFormsEditStore } from "presentation/stores/use-forms-edit.store";
import { Button } from "primereact/button";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import ManageQuestionsGrid from "./ManageQuestionsGrid";
import { Dialog } from "primereact/dialog";
import ManageQuestion from "./ManageQuestion";
import { QuestionTypes } from "domain/enum/questionTypes";
import { formTemplateUpdateInput } from "infra/repositories/formstemplate-repository";
import CreateInstance from "./CreateInstance";


/**
 * Props do componente {@link FormEdit}.
 *
 * @property guid        - GUID do template a editar. Se for `null` ou inválido, o componente entra
 *                         em modo de criação (gera um novo UUID local).
 * @property onClose     - Callback executado quando a edição/criação termina com sucesso, tipicamente
 *                         para fechar o diálogo/painel onde este componente está embutido.
 * @property refetchGrid - Callback para forçar o refetch da grelha que listou este formulário
 *                         (chamado após salvar/criar para refletir as alterações).
 */
type FormEditProps = {
    guid: string | null;

    onCloseSuccess: () => void;
    onClose?: () => void;
    refetchGrid: () => void;
}

/**
 * Payload usado para CRIAR uma pergunta (POST). Não inclui `id` porque o backend é responsável
 * por o gerar.
 *
 * @property label           - Texto/enunciado da pergunta apresentado ao respondente.
 * @property type            - Tipo da pergunta (ver {@link QuestionTypes}: TEXT, MULTIPLE, etc.).
 * @property order           - Ordem de apresentação da pergunta no formulário.
 * @property required        - Se a pergunta é de resposta obrigatória.
 * @property active          - Se a pergunta está ativa (visível ao respondente).
 * @property templateId      - GUID do template a que esta pergunta pertence.
 * @property questionOptions - Opções disponíveis (apenas relevante para tipos de escolha múltipla).
 */
export type FormQuestionPayload = {
    label: string;
    type: string;
    order: number;
    required: boolean;
    active: boolean;
    templateId: string;
    questionOptions: FormQuestionOptionPayload[];
}

/**
 * Payload de uma opção de resposta (usada em perguntas de escolha múltipla).
 *
 * @property id    - Identificador único da opção (gerado no cliente; mantido na atualização).
 * @property value - Texto apresentado para a opção.
 */
export type FormQuestionOptionPayload = {
    id: string;
    value: string;
}

/**
 * Payload usado para ATUALIZAR uma pergunta existente (PUT/PATCH).
 * Estende {@link FormQuestionPayload} adicionando o `id` da pergunta a atualizar.
 */
export type FormQuestionUpdatePayload = {
    id: string;
} & FormQuestionPayload;

/**
 * Componente de edição/criação de um formulário (Template + Perguntas + Instância).
 *
 * @param props - {@link FormEditProps}
 * @returns JSX com o painel de edição, ou um {@link Spinner} enquanto carrega os dados iniciais.
 */
const FormEdit = ({guid, onCloseSuccess, refetchGrid, onClose}: FormEditProps) => {

    // ─────────────────────────────────────────────────────────────────────────
    // Estado local do componente
    // ─────────────────────────────────────────────────────────────────────────

    /** Mutation para atualizar a Instância (não o Template). Usada por {@link updateInstance}. */
    const updateInstanceMutation = useUpdateFormsInstanceMutation();

    /** Acesso ao store global (Zustand) que mantém o "rascunho" da edição. */
    const state = useFormsEditStore();
    /** Referência ao Toast do PrimeReact para feedback inline (erro/sucesso). */
    const toast = useRef<Toast>(null);
    const [instances, setInstances] = useState<FormInstance[]>([]);
    const [instanceLinksVisible, setInstanceLinksVisible] = useState(false);

    /**
     * IDs das perguntas que vieram da BD (no momento do carregamento).
     * Serve para distinguir, no momento de salvar, quais perguntas devem ser ATUALIZADAS
     * (estão em `questionIdFromDB`) e quais devem ser CRIADAS (não estão).
     */
    const [questionIdFromDB, setQuestionIdFromDB] = useState<string[]>([]);
    /** Link público completo para responder ao formulário (construído a partir do `slug` da instância). */
    const [formLink, setFormLink] = useState<string>("");
    /** Controla a visibilidade do diálogo de criação de Instância ({@link CreateInstance}). */
    const [generateInstanceVisible, setGenerateInstanceVisible] = useState<boolean>(false);

    // ─────────────────────────────────────────────────────────────────────────
    // Mutations de Template e Perguntas
    // ─────────────────────────────────────────────────────────────────────────
    const saveTemplateMutation = useCreateTemplateMutation();
    const createQuestionsMutation = useCreateQuestionMutation();
    const updateTemplateMutation = useUpdateTemplateMutation();
    const deleteQuestionMutation = useDeleteQuestionMutation();
    const updateQuestionMutation = useUpdateQuestionMutation();

    /**
     * Valida um GUID/UUID v1-v5 segundo a RFC 4122.
     *
     * @param uuid - String a validar.
     * @returns `true` se a string corresponde ao formato GUID; `false` caso contrário.
     */
    const isValidGuid = (uuid: string): boolean => {
        const guidRegex =
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;

        return guidRegex.test(uuid);
    }

    /**
     * Atualiza o estado `active` da instância atual, tanto no store local como na BD.
     *
     * Nota: atualmente este método está implementado mas NÃO é chamado pela UI
     * (a ativação acontece quando uma nova instância é gerada via {@link CreateInstance}).
     *
     * @param status - Novo valor para o campo `active` da instância.
     */
    const updateInstance = async (status: boolean) => {
        state.setFormsEdit({instance: {...state.formData.instance, active: status}});
        const updatedInstance: FormInstanceUpdate = {
            isAnonymous: state.formData.instance.isAnonymous,
            description: state.formData.instance.description,
            logo: state.formData.instance.logo
        };
        await updateInstanceMutation.mutateAsync({instance: updatedInstance, id: state.formData.instance.id as string});
    }

    /**
     * Indica se o `guid` recebido nas props é válido — i.e., se estamos em modo EDIÇÃO.
     * Se for `false`, o componente assume modo CRIAÇÃO.
     */
    const isValidId: boolean = (guid !== null && isValidGuid(guid));
    

    // ─────────────────────────────────────────────────────────────────────────
    // Queries (carregamento em cascata: template → instance + questions)
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Busca o template pelo GUID. Se não estivermos em modo edição, passa-se string vazia
     * para evitar requests inúteis (o hook deve tratar como no-op).
     */
    const {
        data: formTemplateById,
        isLoading: isTemplateLoading,
        isError,
        refetch,
    } = useFormTemplateById(isValidId ? guid as string : "");

    /** ID do template carregado — usado como chave para as duas queries dependentes abaixo. */
    const templateId = formTemplateById?.data?.id;

    /**
     * Busca a última instância criada para este template (ordenada por createdAt desc).
     * Apenas é executada quando `templateId` está disponível.
     */
    const {
        data: formInstance,
        isLoading: isFormInstanceLoading,
        refetch: refetchInstance,
    } = useGetInstanceByFilter(
        { templateId: templateId,
            sortOrder: "desc",
            sortBy: "createdAt"
        } as FormInstanceInputFilter,
        {
            enabled: !!templateId,
            retry: false
        });

    /**
     * Busca todas as perguntas (e respetivas opções) associadas a este template.
     * Apenas é executada quando `templateId` está disponível.
     */
    const {
        data: questions,
        isLoading: isQuestionsLoading,
        isError: isQuestionsError,
        error: errorQuestions,
        refetch: refetchQuestions
    } = useGetQuestionByFilter({formTemplateId: templateId} as QuestionInputFilter, {enabled: !!templateId});

    // ─────────────────────────────────────────────────────────────────────────
    // Effects — sincronização entre queries/props e o store global
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Effect [mount]: limpa o store de qualquer estado residual de uma edição anterior.
     * Executado uma única vez quando o componente é montado.
     */
    useEffect(() => {
        state.resetFormsData();
    }, [])

    /**
     * Effect: quando o template é carregado da BD (modo edição), copia-o para o store.
     * Em modo criação (`!isValidId`), este effect não faz nada — o template é inicializado
     * pelo último useEffect deste bloco.
     */
    useEffect(() => {
        if(!isTemplateLoading && isValidId){
            state.setFormsEdit({formTemplate: formTemplateById?.data});
        }
    }, [formTemplateById, isTemplateLoading, isValidId])

    /**
     * Effect: quando a query de instâncias resolve com pelo menos um resultado, guarda a primeira
     * (a mais recente) no store e calcula o link público para a resposta ao formulário.
     */
    useEffect(() => {
    if (
        !isFormInstanceLoading &&
        formInstance &&
        formInstance.data &&
        formInstance.data.length > 0
    ) {
        setInstances(formInstance.data);

        const latestInstance = formInstance.data[0];

        state.setFormsEdit({ instance: latestInstance });

        setFormLink(
            `${window.location.origin}/respondToForm/${latestInstance.slug}`
        );
    }
}, [isFormInstanceLoading, formInstance]);

    /**
     * Effect: recalcula o `formLink` sempre que o `slug` da instância mudar no store.
     * Necessário porque o slug pode ser definido após gerar uma NOVA instância (via {@link CreateInstance}),
     * sem que a query original tenha sido refeita.
     */
    useEffect(() => {
        if(state.formData.instance.slug){
            const formLink = `${window.location.origin}/respondToForm/${state.formData.instance.slug ?? ""}`;
            setFormLink(formLink);
        }
    }, [state.formData.instance]);

    /**
     * Effect: quando as perguntas são carregadas da BD, popula o store com:
     *  - a lista de perguntas,
     *  - o total,
     *  - todas as opções (achatadas a partir de cada pergunta).
     * Também regista os IDs originais em `questionIdFromDB` para depois saber quais
     * perguntas existem realmente na BD (ver {@link getUpdatedQuestionsPayload}).
     */
    useEffect(() => {
        if(!isQuestionsLoading && questions && questions.data){
            const optionsArr = questions.data.flatMap((q: FormQuestion) => q.questionOptions ?? []);
            setQuestionIdFromDB(questions.data.map((q: FormQuestion) => q.id));
            state.setFormsEdit({
                questions: questions.data,
                totalQuestions: questions.data.length,
                questionOptions: optionsArr
            });
        }
    }, [isQuestionsLoading, questions, questions?.data])

    /**
     * Effect: em modo CRIAÇÃO (GUID inválido), inicializa um template vazio no store
     * com um novo UUID gerado no cliente, mas apenas se ainda não houver um inicializado
     * (evita sobrescrever em re-renders).
     */
    useEffect(() => {
        if (isValidId)
            return;

        // already initialized
        if (state.formData.formTemplate.id)
            return;

        state.setFormsEdit({
            formTemplate: {
                id: crypto.randomUUID(),
                title: "",
                description: "",
                config: "",
                active: false,
                createdAt: new Date(),
                modifiedAt: null
            }
        });
    }, [isValidId]);

    // Enquanto qualquer query inicial estiver a carregar, mostra um spinner global.
    if(isTemplateLoading || isFormInstanceLoading || isQuestionsLoading) return <Spinner />

    // ─────────────────────────────────────────────────────────────────────────
    // Persistência: criação e atualização
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Cria um novo formulário (template + todas as suas perguntas).
     *
     * Fluxo:
     *  1. Persiste o template via `saveTemplateMutation`;
     *  2. Se o backend não devolver `data`, mostra erro e aborta;
     *  3. Atualiza o `templateId` em todas as perguntas no store;
     *  4. Cria TODAS as perguntas em bloco (sem filtro).
     *
     * Usado apenas quando `isValidId === false`.
     */
    const createNewForm = async () => {
        const { title, description, config, active } = state.formData.formTemplate;
        const template = await saveTemplateMutation.mutateAsync({ title, description, config, active });

        if (!template.data) {
            showToastError("Erro inesperado ao criar template na BD");
            return;
        }

        giveTemplateIdToQuestions(template.data.id);

        await createQuestionsMutation.mutateAsync(populateQuestionPayload(template.data.id));
    }

    /**
     * Atualiza um formulário existente.
     *
     * Fluxo:
     *  1. Atualiza os metadados do template (title/description/config/active);
     *  2. Garante que todas as perguntas referenciam o `templateId` correto;
     *  3. Elimina (em paralelo) as perguntas marcadas para remoção no store
     *     (`deletedQuestionIds`);
     *  4. Em paralelo:
     *      - atualiza as perguntas que JÁ existiam na BD (estão em `questionIdFromDB`);
     *      - cria as perguntas adicionadas durante esta sessão de edição
     *        (estão em `state.formData.addedQuestions`).
     *
     * Usado apenas quando `isValidId === true`.
     */
    const updateForm = async () => {
        const { title, description, config, active } = state.formData.formTemplate;
        const templateInput: formTemplateUpdateInput = { id: guid as string, title, description, config, active };

        await updateTemplateMutation.mutateAsync(templateInput);

        giveTemplateIdToQuestions(guid as string);

        await deleteFormQuestions();

        await updateFormQuestions();
    }

    const deleteFormQuestions = async () => {
        await Promise.all(
            state.formData.deletedQuestionIds.map(id => deleteQuestionMutation.mutateAsync(id))
        );
    }

    const updateFormQuestions = async () => {
        await Promise.all([
            updateQuestionMutation.mutateAsync(getUpdatedQuestionsPayload(guid as string)),
            createQuestionsMutation.mutateAsync(populateQuestionPayload(guid as string, state.formData.addedQuestions)),
        ]);
    }

    const validateForm = (): boolean => {
        if(state.formData.formTemplate.title.trim() === ""){
            showToastError("Por favor introduza um nome para o formulário");
            return false;
        }

        if (!state.formData.questions || state.formData.questions.length === 0) {
            showToastError("Por favor crie pelo menos uma pergunta");
            return false;
        }

        if (areThereQuestionWithoutOptions()) {
            showToastError("Por favor adicione pelo menos uma opção a cada questão de escolha múltipla");
            return false;
        }

        return true;
    }
    /**
     * Valida o formulário e dispara a operação correta (criação ou atualização).
     *
     * Regras de validação aplicadas (por ordem):
     *  1. O título não pode estar vazio.
     *  2. Tem de existir pelo menos uma pergunta.
     *  3. Opções "órfãs" (sem pergunta correspondente) são removidas silenciosamente.
     *  4. Toda a pergunta do tipo `MULTIPLE` tem de ter, pelo menos, uma opção.
     *
     * @returns `true` se o formulário foi salvo com sucesso; `false` se a validação falhou.
     *          Em caso de sucesso, o caller é responsável por chamar `onClose()`.
     */
    const saveForm = async (): Promise<boolean> => {

        if (!validateForm())
            return false;

        clearOptionsWithoutQuestion();

        try {
            if(!isValidId){
                await createNewForm();
                refetchGrid();
                return true;
            }
            await updateForm();
            refetchGrid();
            return true;
        } catch (error: any) {
            showToastError("Erro ao guardar: " + error.message);
            return false;
        }
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Construção de payloads para o backend
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Constrói o payload de UPDATE de perguntas: apenas perguntas cujo ID já existia na BD
     * no momento do carregamento (filtra por `questionIdFromDB`).
     *
     * @param templateId - GUID do template a associar a cada pergunta.
     */
    const getUpdatedQuestionsPayload = (templateId: string): FormQuestionUpdatePayload[] =>
            state.formData.questions
                .filter(q => questionIdFromDB.includes(q.id))
                .map(q => ({
                    id: q.id,
                    label: q.label,
                    templateId,
                    active: q.active,
                    required: q.required,
                    order: q.order,
                    type: q.type,
                    questionOptions: buildQuestionOptions(q.id),
                }));

    /**
     * Constrói o payload de CREATE de perguntas.
     *
     * @param templateId - GUID do template ao qual as perguntas pertencem.
     * @param filterIds  - Se fornecido, restringe a apenas as perguntas cujo ID consta na lista
     *                     (tipicamente `addedQuestions`, no fluxo de update). Se omitido, inclui
     *                     todas as perguntas do store (fluxo de create-from-scratch).
     */
    const populateQuestionPayload = (templateId: string, filterIds?: string[]): FormQuestionPayload[] =>
            state.formData.questions
                .filter(q => !filterIds || filterIds.includes(q.id))
                .map(q => ({
                    label: q.label,
                    templateId,
                    active: q.active,
                    required: q.required,
                    order: q.order,
                    type: q.type,
                    questionOptions: buildQuestionOptions(q.id),
                }));

    /**
     * Devolve as opções pertencentes a uma pergunta, no formato do payload.
     *
     * @param questionId - ID da pergunta cujas opções pretendemos.
     */
    const buildQuestionOptions = (questionId: string): FormQuestionOptionPayload[] =>
            state.formData.questionOptions
                .filter(opt => opt.questionId === questionId)
                .map(opt => ({ id: opt.id, value: opt.value }));

    // ─────────────────────────────────────────────────────────────────────────
    // Helpers de UI (toasts) e de validação/sanitização
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Mostra um toast de erro (vermelho) com a mensagem dada. Vida: 1500ms.
     */
    const showToastError = (detail: string) => {
        toast.current?.show({ severity: "error", summary: "Erro", detail, life: 1500 });
    };

    /**
     * Mostra um toast de sucesso (verde) com a mensagem dada. Vida: 1500ms.
     */
    const showToastSuccess = (detail: string) => {
        toast.current?.show({ severity: "success", summary: "Sucesso", detail, life: 1500 });
    };

    /**
     * Indica se a pergunta com o ID dado tem PELO MENOS uma opção associada no store.
     */
    const doesQuestionHaveOptions = (questionId: string): boolean =>
        state.formData.questionOptions.some(opt => opt.questionId === questionId);

    /**
     * Indica se existe alguma pergunta de tipo `MULTIPLE` sem opções — caso em que a
     * persistência deve ser bloqueada.
     */
    const areThereQuestionWithoutOptions = (): boolean =>
        state.formData.questions.some(q => q.type === QuestionTypes.MULTIPLE && !doesQuestionHaveOptions(q.id));


    /**
     * Remove do store as opções que não estão associadas a nenhuma pergunta existente
     * (opções "órfãs", tipicamente resultado de eliminar uma pergunta com opções).
     */
    const clearOptionsWithoutQuestion = () => {
        const questionIds = state.formData.questions.map(q => q.id);
        state.setFormsEdit({
            questionOptions: state.formData.questionOptions.filter(opt => questionIds.includes(opt.questionId)),
        });
    };

    /**
     * Reatribui o `templateId` a TODAS as perguntas no store.
     * Necessário porque, em modo criação, o template só recebe o seu ID definitivo
     * depois de ser persistido — antes disso as perguntas têm um `templateId` provisório.
     *
     * @param id - O `templateId` definitivo a propagar.
     */
    const giveTemplateIdToQuestions = (id: string) => {
        state.setFormsEdit({
            questions: state.formData.questions.map(q => ({ ...q, templateId: id })),
        });
    };
    

    // ─────────────────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────────────────
    //
    // Estrutura visual atual (de cima para baixo):
    //   1. Barra superior alinhada à direita: botão "Adicionar"/"Salvar".
    //   2. Linha em duas colunas (lado a lado):
    //        Coluna A: Título + (Gerar Instância | Copiar Link)
    //        Coluna B: Descrição (textarea)
    //   3. Grelha de gestão de perguntas ({@link ManageQuestionsGrid}).
    //   4. Diálogo modal para gerar nova Instância ({@link CreateInstance}).
    //   5. Toast (para feedback ao utilizador).
    return (
        <div style={{display: "flex", flexDirection: "column", height: "100%"}}>

        {/* Bloco principal: cabeçalho do formulário (título, ações de instância, descrição). */}
        <div style={{ paddingTop: '1.5rem', display: "flex", flexDirection: "column", gap: "1rem" }}>
            {/* Linha 1: Título + Gerar Instância + Copiar Link */}
            <div style={{ display: "flex", gap: "1rem", alignItems: "flex-end" }}>
                <FloatLabel style={{ flex: 3 }}>
                    <InputText
                    id="title"
                    value={state.formData.formTemplate.title}
                    style={{width: "100%"}}
                    onChange={(e) => {
                        state.setFormsEdit({formTemplate: {...state.formData.formTemplate, title: e.target.value}});
                    }}/>
                    <label htmlFor="title">Título</label>
                </FloatLabel>

                <div style={{ flex: 2, display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    <label htmlFor="actions">Ações da Instância</label>
                    <div style={{ display: "flex", flexDirection: "row", gap: "0.5rem" }}>
                        <Button
                        disabled={!isValidId}
                        style={{flex: 1}}
                        label="Registar Instância"
                        onClick={async () => {
                            setGenerateInstanceVisible(true);
                        }}
                        />
                       {instances.length > 0 && (
                         <Button
                        style={{ width: "3rem" }}
                        icon="pi pi-copy"
                        tooltip="Ver links das instâncias"
                       
                        onClick={() => setInstanceLinksVisible(true)}
                        />)}
                    </div>
                </div>
            </div>
                       <Dialog
    header="Links das Instâncias"
    visible={instanceLinksVisible}
    style={{ width: "60vw" }}
    onHide={() => setInstanceLinksVisible(false)}
>
    <div
        style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.75rem"
        }}
    >
        {instances.map((instance) => {
            const link =
                `${window.location.origin}/respondToForm/${instance.slug}`;

            return (
                <div
                    key={instance.id}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem"
                    }}
                >
                    <InputText
                        value={link}
                        readOnly
                        style={{ flex: 1 }}
                    />

                    <Button
                        icon="pi pi-copy"
                       
                        onClick={() => {
                            navigator.clipboard.writeText(link);

                            toast.current?.show({
                                severity: "success",
                                summary: "Sucesso",
                                detail: "Link copiado",
                                life: 1500
                            });
                        }}
                    />
                </div>
            );
        })}
    </div>
</Dialog>
            {/* Linha 2: Descrição em 100% da largura */}
            <FloatLabel style={{ width: "100%", marginTop: "1rem" }}>
                <InputTextarea
                id="description"
                rows={ 2 }
                style={{width: "100%", marginBottom: "1rem", resize: "none" }}
                value={state.formData.formTemplate.description}
                onChange={(e) => {
                    state.setFormsEdit({formTemplate: {...state.formData.formTemplate, description: e.target.value}});
                }}/>
                <label htmlFor="description">Descrição</label>
            </FloatLabel>
        </div>

        {/* Grelha onde o utilizador adiciona/edita/remove perguntas e respetivas opções. */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0,}}>
            {/* Separador visual entre o cabeçalho do formulário e a grelha de perguntas. */}
            <hr style={{ border: "none", borderTop: "1px solid var(--surface-border)", margin: "0.5rem 0" }} />
            <ManageQuestionsGrid />
        </div>

        {/* Barra de ação principal: cancelar + botão dinâmico (Adicionar vs Salvar), lado a lado. */}
        <div style={
            {
                width: "100%",
                display: "flex", 
                justifyContent: "flex-end", 
                alignItems: "center", 
                gap: "0.5rem", 
                marginTop: "0.5rem",
                paddingTop: "1rem"
            }
            }>
            
            <Button
            type="button"
            severity="secondary"
            outlined
            label="Cancelar"
            onClick={onClose}
            />
            <Button
            type="button"
            severity="info"
            label={(!isValidId) ? "Adicionar" : "Salvar"}
            onClick={async () => {
                const success = await saveForm();
                if (success) {
                    onCloseSuccess();
                }
            }}
            />
        </div>

        {/* Diálogo modal para gerar uma nova instância (publicar) associada ao template atual. */}
        <Dialog
        visible={generateInstanceVisible}
        header="Criar Instância"
        style={{width: "50%"}}
        onHide={() => setGenerateInstanceVisible(false)}
        >
            <CreateInstance
    close={async () => {
        await refetchInstance();
        setGenerateInstanceVisible(false);
        showToastSuccess("Instância gerada com sucesso!");
    }}
    templateId={state.formData.formTemplate.id}
/>
        </Dialog>

        {/* Toast global do componente — utilizado por showToastError/showToastSuccess. */}
        <Toast ref={toast} />
        </div>
    )
}

export default FormEdit;